import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CATEGORIES = [
  { key: "necessary", title: "Strictly necessary", text: "Remembers your cookie choices and keeps the site secure. Always on.", locked: true },
  { key: "analytics", title: "Analytics", text: "Google Analytics 4: page views, traffic sources and how the site is used." },
  { key: "advertising", title: "Advertising", text: "Meta Pixel and Google Ads: measure signups from our ads and show relevant ads on other sites and apps." },
];

const BTN = "min-h-11 py-2 w-full sm:flex-1 rounded-full px-5 font-heading text-sm font-extrabold transition-[background-color] duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40";

export const CookiePreferences = ({ open, onOpenChange, current, onSave }) => {
  const [prefs, setPrefs] = useState({ analytics: false, advertising: false });
  const gpc = typeof navigator !== "undefined" && Boolean(navigator.globalPrivacyControl);

  useEffect(() => {
    if (open) setPrefs({ analytics: Boolean(current?.analytics), advertising: Boolean(current?.advertising) });
  }, [open, current]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cookie-preferences-modal" className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto rounded-[2rem] border-0 bg-cream p-6 sm:p-8">
        <DialogTitle className="font-heading text-2xl font-extrabold text-navy">Cookie Settings</DialogTitle>
        <DialogDescription className="text-sm leading-relaxed text-navy/80">
          Choose which optional technologies you allow. Optional categories stay off unless you turn them on.
        </DialogDescription>
        <ul className="mt-2 space-y-3">
          {CATEGORIES.map((c) => (
            <li key={c.key} className="flex items-start justify-between gap-4 rounded-2xl bg-white p-4">
              <div>
                <label htmlFor={`consent-${c.key}`} className="font-heading font-extrabold text-navy">{c.title}</label>
                <p className="mt-1 text-sm leading-relaxed text-navy/75">{c.text}</p>
                {c.key === "advertising" && gpc && (
                  <p data-testid="cookie-gpc-notice" className="mt-2 text-xs font-semibold text-teal-dark">Your browser's Global Privacy Control signal is on, so advertising stays off.</p>
                )}
              </div>
              <Switch
                id={`consent-${c.key}`}
                data-testid={`cookie-toggle-${c.key}`}
                checked={c.locked ? true : prefs[c.key]}
                disabled={c.locked || (c.key === "advertising" && gpc)}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                className="mt-1 data-[state=checked]:bg-teal"
              />
            </li>
          ))}
        </ul>
        <div className="mt-2 flex flex-col gap-2.5 sm:flex-row">
          <button type="button" data-testid="cookie-save-preferences" onClick={() => onSave(prefs)} className={`${BTN} bg-teal text-white hover:bg-teal-dark`}>Save choices</button>
          <button type="button" data-testid="cookie-prefs-reject" onClick={() => onSave({ analytics: false, advertising: false })} className={`${BTN} bg-navy text-cream hover:bg-[#0c2644]`}>Reject Nonessential</button>
          <button type="button" data-testid="cookie-prefs-accept" onClick={() => onSave({ analytics: true, advertising: true })} className={`${BTN} bg-navy text-cream hover:bg-[#0c2644]`}>Accept All</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
